/**
 * Coletor de Fingerprint
 * Responsável por orquestrar a coleta de todos os dados de fingerprint
 */

import { CanvasService } from './CanvasService.js';
import { WebGLService } from './WebGLService.js';
import { AudioService } from './AudioService.js';
import { BehavioralService } from './BehavioralService.js';
import { DeviceService } from './DeviceService.js';
import { PrivacyService } from './PrivacyService.js';
import { NetworkService } from './NetworkService.js';
import GeolocationService from './GeolocationService.js';

export class FingerprintCollector {
    constructor(config, logger, errorHandler) {
        this.config = config;
        this.logger = logger.child('FingerprintCollector');
        this.errorHandler = errorHandler;
        
        // Inicializa serviços
        this.services = {
            canvas: new CanvasService(config, logger),
            webgl: new WebGLService(config, logger),
            audio: new AudioService(config, logger),
            behavioral: new BehavioralService(config, logger),
            device: new DeviceService(config, logger),
            privacy: new PrivacyService(config, logger),
            network: new NetworkService(config, logger),
            geolocation: new GeolocationService(config, logger)
        };
        
        this.stats = {
            collectionsPerformed: 0,
            successfulCollections: 0,
            failedCollections: 0,
            averageCollectionTime: 0,
            lastCollectionTime: null,
            serviceStats: {}
        };
        
        this.collectionStrategies = {
            fast: {
                timeout: 5000,
                services: ['device', 'canvas', 'webgl'],
                parallel: true
            },
            complete: {
                timeout: 15000,
                services: ['device', 'canvas', 'webgl', 'audio', 'privacy', 'network', 'geolocation'],
                parallel: true
            },
            behavioral: {
                timeout: 30000,
                services: ['device', 'canvas', 'webgl', 'audio', 'behavioral', 'privacy', 'network'],
                parallel: false
            }
        };
    }
    
    /**
     * Coleta fingerprint completo
     */
    async collectFingerprint(strategy = 'complete') {
        const startTime = performance.now();
        this.stats.collectionsPerformed++;
        
        try {
            const strategyConfig = this.collectionStrategies[strategy];
            if (!strategyConfig) {
                throw new Error(`Estratégia de coleta '${strategy}' não encontrada`);
            }
            
            this.logger.info('Iniciando coleta de fingerprint', { strategy });
            
            const fingerprint = {
                // Metadados
                metadata: {
                    strategy,
                    timestamp: Date.now(),
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    referrer: document.referrer,
                    collectionId: this.generateCollectionId()
                },
                
                // Dados coletados
                data: {},
                
                // Estatísticas de coleta
                stats: {
                    startTime,
                    endTime: null,
                    duration: null,
                    servicesUsed: strategyConfig.services,
                    errors: []
                }
            };
            
            // Executa coleta baseada na estratégia
            if (strategyConfig.parallel) {
                await this.collectParallel(fingerprint, strategyConfig);
            } else {
                await this.collectSequential(fingerprint, strategyConfig);
            }
            
            // Finaliza coleta
            const endTime = performance.now();
            fingerprint.stats.endTime = endTime;
            fingerprint.stats.duration = Math.round(endTime - startTime);
            
            // Gera hash do fingerprint
            fingerprint.hash = await this.generateFingerprintHash(fingerprint.data);
            
            // Atualiza estatísticas
            this.updateStats(fingerprint.stats.duration, true);
            
            this.logger.info('Coleta de fingerprint concluída', {
                strategy,
                duration: fingerprint.stats.duration,
                hash: fingerprint.hash,
                servicesUsed: strategyConfig.services.length,
                errors: fingerprint.stats.errors.length
            });
            
            return fingerprint;
            
        } catch (error) {
            this.updateStats(performance.now() - startTime, false);
            this.logger.error('Erro na coleta de fingerprint', { error: error.message, strategy });
            throw error;
        }
    }
    
    /**
     * Coleta paralela de dados
     */
    async collectParallel(fingerprint, strategyConfig) {
        const promises = strategyConfig.services.map(serviceName => 
            this.collectFromService(serviceName, strategyConfig.timeout)
        );
        
        const results = await Promise.allSettled(promises);
        
        results.forEach((result, index) => {
            const serviceName = strategyConfig.services[index];
            
            if (result.status === 'fulfilled') {
                fingerprint.data[serviceName] = result.value;
            } else {
                fingerprint.stats.errors.push({
                    service: serviceName,
                    error: result.reason.message,
                    timestamp: Date.now()
                });
                this.logger.warn(`Erro no serviço ${serviceName}`, { error: result.reason.message });
            }
        });
    }
    
    /**
     * Coleta sequencial de dados
     */
    async collectSequential(fingerprint, strategyConfig) {
        for (const serviceName of strategyConfig.services) {
            try {
                fingerprint.data[serviceName] = await this.collectFromService(
                    serviceName, 
                    strategyConfig.timeout / strategyConfig.services.length
                );
            } catch (error) {
                fingerprint.stats.errors.push({
                    service: serviceName,
                    error: error.message,
                    timestamp: Date.now()
                });
                this.logger.warn(`Erro no serviço ${serviceName}`, { error: error.message });
            }
        }
    }
    
    /**
     * Coleta dados de um serviço específico
     */
    async collectFromService(serviceName, timeout) {
        const service = this.services[serviceName];
        if (!service) {
            throw new Error(`Serviço '${serviceName}' não encontrado`);
        }
        
        return new Promise(async (resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new Error(`Timeout na coleta do serviço ${serviceName}`));
            }, timeout);
            
            try {
                let result;
                
                switch (serviceName) {
                    case 'canvas':
                        result = await service.generateFingerprint();
                        break;
                    case 'webgl':
                        result = await service.generateFingerprint();
                        break;
                    case 'audio':
                        result = await service.generateFingerprint();
                        break;
                    case 'behavioral':
                        result = service.getBehavioralData();
                        break;
                    case 'device':
                        result = await service.getDeviceInfo();
                        break;
                    case 'privacy':
                        result = await service.detectSuspiciousEnvironment();
                        break;
                    case 'network':
                        result = await service.analyzeNetwork();
                        break;
                    case 'geolocation':
                        result = await service.getCompleteGeolocation();
                        break;
                    default:
                        throw new Error(`Método de coleta não definido para ${serviceName}`);
                }
                
                clearTimeout(timeoutId);
                resolve(result);
                
            } catch (error) {
                clearTimeout(timeoutId);
                reject(error);
            }
        });
    }
    
    /**
     * Gera hash do fingerprint
     */
    async generateFingerprintHash(data) {
        try {
            // Serializa os dados de forma consistente
            const serialized = this.serializeData(data);
            
            // Gera hash usando SubtleCrypto
            if (window.crypto && window.crypto.subtle) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(serialized);
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
            
            // Fallback para hash simples
            return this.simpleHash(serialized);
            
        } catch (error) {
            this.logger.warn('Erro na geração de hash, usando fallback', { error: error.message });
            return this.simpleHash(JSON.stringify(data));
        }
    }
    
    /**
     * Serializa dados de forma consistente
     */
    serializeData(data) {
        // Remove campos que podem variar entre coletas
        const cleanData = this.cleanDataForHashing(data);
        
        // Ordena as chaves recursivamente
        const sortedData = this.sortObjectKeys(cleanData);
        
        return JSON.stringify(sortedData);
    }
    
    /**
     * Limpa dados para hashing
     */
    cleanDataForHashing(data) {
        const cleaned = {};
        
        for (const [key, value] of Object.entries(data)) {
            if (value && typeof value === 'object') {
                if (Array.isArray(value)) {
                    cleaned[key] = value.map(item => 
                        typeof item === 'object' ? this.cleanDataForHashing(item) : item
                    );
                } else {
                    // Remove campos temporais e variáveis
                    const { timestamp, duration, startTime, endTime, ...rest } = value;
                    cleaned[key] = this.cleanDataForHashing(rest);
                }
            } else {
                cleaned[key] = value;
            }
        }
        
        return cleaned;
    }
    
    /**
     * Ordena chaves de objeto recursivamente
     */
    sortObjectKeys(obj) {
        if (Array.isArray(obj)) {
            return obj.map(item => 
                typeof item === 'object' && item !== null ? this.sortObjectKeys(item) : item
            );
        }
        
        if (typeof obj === 'object' && obj !== null) {
            const sorted = {};
            Object.keys(obj).sort().forEach(key => {
                sorted[key] = this.sortObjectKeys(obj[key]);
            });
            return sorted;
        }
        
        return obj;
    }
    
    /**
     * Hash simples para fallback
     */
    simpleHash(str) {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Converte para 32bit
        }
        
        return Math.abs(hash).toString(16);
    }
    
    /**
     * Gera ID único para coleta
     */
    generateCollectionId() {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `${timestamp}-${random}`;
    }
    
    /**
     * Inicia coleta comportamental
     */
    startBehavioralCollection() {
        try {
            this.services.behavioral.startCollection();
            this.logger.info('Coleta comportamental iniciada');
        } catch (error) {
            this.logger.error('Erro ao iniciar coleta comportamental');
            throw error;
        }
    }
    
    /**
     * Para coleta comportamental
     */
    stopBehavioralCollection() {
        try {
            this.services.behavioral.stopCollection();
            this.logger.info('Coleta comportamental parada');
        } catch (error) {
            this.logger.error('Erro ao parar coleta comportamental', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Obtém dados comportamentais atuais
     */
    getBehavioralData() {
        try {
            return this.services.behavioral.getBehavioralData();
        } catch (error) {
            this.logger.error('Erro ao obter dados comportamentais', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Limpa dados comportamentais
     */
    clearBehavioralData() {
        try {
            this.services.behavioral.clearBehavioralData();
            this.logger.info('Dados comportamentais limpos');
        } catch (error) {
            this.logger.error('Erro ao limpar dados comportamentais', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Atualiza estatísticas
     */
    updateStats(duration, success) {
        if (success) {
            this.stats.successfulCollections++;
        } else {
            this.stats.failedCollections++;
        }
        
        // Atualiza tempo médio de coleta
        const totalCollections = this.stats.successfulCollections + this.stats.failedCollections;
        this.stats.averageCollectionTime = (
            (this.stats.averageCollectionTime * (totalCollections - 1) + duration) / totalCollections
        );
        
        this.stats.lastCollectionTime = Date.now();
        
        // Atualiza estatísticas dos serviços
        Object.keys(this.services).forEach(serviceName => {
            const service = this.services[serviceName];
            if (service.getStats) {
                this.stats.serviceStats[serviceName] = service.getStats();
            }
        });
    }
    
    /**
     * Obtém estatísticas do coletor
     */
    getStats() {
        return {
            collector: {
                collectionsPerformed: this.stats.collectionsPerformed,
                successfulCollections: this.stats.successfulCollections,
                failedCollections: this.stats.failedCollections,
                successRate: this.stats.collectionsPerformed > 0 ? 
                    (this.stats.successfulCollections / this.stats.collectionsPerformed * 100).toFixed(2) + '%' : '0%',
                averageCollectionTime: Math.round(this.stats.averageCollectionTime),
                lastCollectionTime: this.stats.lastCollectionTime
            },
            services: this.stats.serviceStats
        };
    }
    
    /**
     * Obtém capacidades dos serviços
     */
    getCapabilities() {
        const capabilities = {};
        
        Object.keys(this.services).forEach(serviceName => {
            const service = this.services[serviceName];
            if (service.constructor.getCapabilities) {
                capabilities[serviceName] = service.constructor.getCapabilities();
            } else if (service.constructor.isSupported) {
                capabilities[serviceName] = {
                    supported: service.constructor.isSupported()
                };
            }
        });
        
        return capabilities;
    }
    
    /**
     * Valida estratégia de coleta
     */
    validateStrategy(strategy) {
        if (!this.collectionStrategies[strategy]) {
            throw new Error(`Estratégia '${strategy}' não é válida. Estratégias disponíveis: ${Object.keys(this.collectionStrategies).join(', ')}`);
        }
        
        const strategyConfig = this.collectionStrategies[strategy];
        const unavailableServices = strategyConfig.services.filter(serviceName => 
            !this.services[serviceName]
        );
        
        if (unavailableServices.length > 0) {
            throw new Error(`Serviços não disponíveis para estratégia '${strategy}': ${unavailableServices.join(', ')}`);
        }
        
        return true;
    }
    
    /**
     * Adiciona estratégia personalizada
     */
    addStrategy(name, config) {
        if (!config.services || !Array.isArray(config.services)) {
            throw new Error('Estratégia deve ter uma lista de serviços');
        }
        
        if (!config.timeout || typeof config.timeout !== 'number') {
            throw new Error('Estratégia deve ter um timeout válido');
        }
        
        this.collectionStrategies[name] = {
            timeout: config.timeout,
            services: config.services,
            parallel: config.parallel !== false // Default true
        };
        
        this.logger.info('Estratégia personalizada adicionada', { name, config });
    }
    
    /**
     * Remove estratégia
     */
    removeStrategy(name) {
        if (['fast', 'complete', 'behavioral'].includes(name)) {
            throw new Error('Não é possível remover estratégias padrão');
        }
        
        delete this.collectionStrategies[name];
        this.logger.info('Estratégia removida', { name });
    }
    
    /**
     * Lista estratégias disponíveis
     */
    getStrategies() {
        return Object.keys(this.collectionStrategies).map(name => ({
            name,
            ...this.collectionStrategies[name]
        }));
    }
    
    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        
        // Atualiza configuração de todos os serviços
        Object.values(this.services).forEach(service => {
            if (service.updateConfig) {
                service.updateConfig(newConfig);
            }
        });
        
        this.logger.debug('Configuração do FingerprintCollector atualizada');
    }
    
    /**
     * Limpa caches de todos os serviços
     */
    clearCaches() {
        Object.values(this.services).forEach(service => {
            if (service.clearCache) {
                service.clearCache();
            }
        });
        
        this.logger.debug('Caches de todos os serviços limpos');
    }
    
    /**
     * Testa suporte do coletor
     */
    static isSupported() {
        return !!(navigator && window && document && performance);
    }
    
    /**
     * Limpa recursos
     */
    destroy() {
        // Para coleta comportamental se estiver ativa
        try {
            this.stopBehavioralCollection();
        } catch (error) {
            // Ignora erro se não estiver coletando
        }
        
        // Destrói todos os serviços
        Object.values(this.services).forEach(service => {
            if (service.destroy) {
                service.destroy();
            }
        });
        
        this.logger.debug('FingerprintCollector destruído');
    }
}